import { colors } from '@cabonline/ui-shared';
import { Text } from '@cabonline/ui-web';
import { useLocale } from '@multiplatform-apps/service-trips';
import { RefObject, useCallback, useEffect, useRef } from 'react';

import { useLayout, useModal } from '../../context';
import { LayoutHeader } from './layout-header';
import { LayoutMain } from './layout-main';
import { StyledGoToContentButton } from './styles';
import { LayoutContentProps, LayoutProps, UseSkipToContentOptions } from './types';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const focusFirstInRef = (ref: RefObject<HTMLElement | null>): void => {
  const first = ref.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
  first?.focus();
};

const useSkipToContent = ({
  isButtonVisible,
  isAnyModalOpen,
  setButtonVisible,
  setHasButtonBeenFocused,
  hasButtonBeenFocused,
  buttonRef,
}: UseSkipToContentOptions) => {
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const shouldShow = event.key === 'Tab' && !isButtonVisible && !isAnyModalOpen;
      if (shouldShow) {
        setButtonVisible(true);
        event.preventDefault();
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      setHasButtonBeenFocused(true);
      event.preventDefault();
    };

    if (!hasButtonBeenFocused) {
      window.addEventListener('keydown', handleKeyDown);
      window.addEventListener('mousedown', handleMouseDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('mousedown', handleMouseDown);
    };
  }, [hasButtonBeenFocused, isAnyModalOpen, isButtonVisible, setButtonVisible, setHasButtonBeenFocused]);

  useEffect(() => {
    if (isButtonVisible) {
      buttonRef.current?.focus();
      setHasButtonBeenFocused(true);
    }
  }, [isButtonVisible, buttonRef, setHasButtonBeenFocused]);
};

const useLayoutBehavior = () => {
  const { isAnyModalOpen } = useModal();
  const { isButtonVisible, setButtonVisible, hasButtonBeenFocused, setHasButtonBeenFocused } = useLayout();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const layoutMainRef = useRef<HTMLDivElement>(null);

  useSkipToContent({
    isButtonVisible,
    isAnyModalOpen,
    setButtonVisible,
    setHasButtonBeenFocused,
    hasButtonBeenFocused,
    buttonRef,
  });

  const focusFirstElementInMain = useCallback(
    () => focusFirstInRef(layoutMainRef),
    []
  );

  return { isButtonVisible, setButtonVisible, buttonRef, layoutMainRef, focusFirstElementInMain };
};

const LayoutContent: React.FC<LayoutContentProps> = ({ children, removePadding }) =>
  removePadding ? <main>{children}</main> : <LayoutMain>{children}</LayoutMain>;

export const Layout: React.FC<LayoutProps> = ({ children, removePadding }) => {
  const { isButtonVisible, setButtonVisible, buttonRef, layoutMainRef, focusFirstElementInMain } =
    useLayoutBehavior();
  const { t } = useLocale();

  return (
    <>
      <StyledGoToContentButton
        ref={buttonRef}
        onBlur={() => setButtonVisible(false)}
        isButtonVisible={isButtonVisible}
        onClick={focusFirstElementInMain}
      >
        <Text color={colors.white}>{t('navbar.goToContentButton')}</Text>
      </StyledGoToContentButton>
      <LayoutHeader />
      <div ref={layoutMainRef}>
        <LayoutContent removePadding={removePadding}>{children}</LayoutContent>
      </div>
    </>
  );
};
